<template>
  <div v-if="localShow" class="modal-overlay" @click="closeModal">
    <div class="modal-dialog" @click.stop>
      <BaseDialog
        :title="'Add to Profiling'"
        :footerButtons="['Cancel', 'Add']"
        @cancel="closeModal"
        @footer-click="handleFooterClick"
      >
        <div class="form-grid">
          <div class="form-group">
            <label for="profiling-select" class="required">Profiling Session</label>
            <select
              id="profiling-select"
              v-model="selectedProfileId"
              class="select"
            >
              <option disabled :value="null">Select Profiling Session</option>
              <option
                v-for="profile in profiles"
                :key="profile.id"
                :value="profile.id"
              >
                {{ profile.name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Selected Validation Sessions</label>
            <div class="val-list-parent-wrap">
              <ValiListMini
                :sessions="selectedSessionsForCards"
                :emptyText="'No validation sessions selected.'"
              />
            </div>
          </div>

          <div class="form-group">
            <label>Validation Sessions In Selected Profile</label>
            <div class="val-list-parent-wrap">
              <ValiListMini
                :sessions="profileValSessions"
                :emptyText="'Selected profile has no validation sessions yet.'"
              />
            </div>
          </div>
        </div>
      </BaseDialog>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import BaseDialog from '@/components/DilaogTemplate/BaseDialog.vue';
import ValiListMini from '@/components/ValidationPage/ValiListMini.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const emit = defineEmits(['added']);

const localShow = ref(false);
const profiles = ref([]);
const selectedProfileId = ref(null);
const selectedSessions = ref([]);

const dataConnect = new DatastoreConnect();
const $toast = useToast();

function extractProfiles(result) {
  if (Array.isArray(result)) {
    return result;
  }

  if (Array.isArray(result?.tasks)) {
    return result.tasks;
  }

  return [];
}

function normalizeProfile(entry) {
  return {
    ...entry,
    id: entry?.id,
    name: entry?.name || entry?.profile_name || (entry?.id != null ? `Profile ${entry.id}` : 'Unnamed Profile'),
  };
}

const selectedProfile = computed(() => (
  profiles.value.find((item) => Number(item.id) === Number(selectedProfileId.value)) || null
));

function getSessionIdValue(session) {
  return Number(
    session?.id
    || session?.validate_session_id
    || session?.val_session_id
    || 0,
  );
}

function getTaskValSessions(task) {
  if (!task || typeof task !== 'object') {
    return [];
  }

  if (Array.isArray(task.val_sessions)) {
    return task.val_sessions;
  }

  if (Array.isArray(task.vals_sessions)) {
    return task.vals_sessions;
  }

  if (Array.isArray(task.val_session)) {
    return task.val_session;
  }

  return [];
}

const profileValSessions = computed(() => {
  const profile = selectedProfile.value;
  if (!profile) {
    return [];
  }

  return getTaskValSessions(profile);
});

const selectedSessionsForCards = computed(() => {
  const sourceSessions = profileValSessions.value;
  const sourceById = new Map(
    sourceSessions.map((entry) => [getSessionIdValue(entry), entry]),
  );

  return selectedSessions.value.map((session) => {
    const id = getSessionIdValue(session);
    const source = sourceById.get(id);
    return source ? { ...source, ...session } : session;
  });
});

function getSessionId(session) {
  const parsed = getSessionIdValue(session);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

async function loadProfiles() {
  const response = await dataConnect.api('profile.list', {});
  if (response?.error) {
    throw new Error(response.error?.message || 'Failed to retrieve profiling sessions');
  }

  profiles.value = extractProfiles(response?.result).map(normalizeProfile).filter((item) => item.id != null);
  selectedProfileId.value = profiles.value.length > 0 ? profiles.value[0].id : null;
}

async function showModal(sessions = []) {
  selectedSessions.value = Array.isArray(sessions) ? sessions : [];

  if (selectedSessions.value.length === 0) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'Please select at least one validation session',
      type: 'warning',
      position: 'top',
    });
    return;
  }

  try {
    await loadProfiles();
    localShow.value = true;
  } catch (error) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: String(error || 'Failed to load profiling sessions'),
      type: 'error',
      position: 'top',
    });
  }
}

function closeModal() {
  localShow.value = false;
}

function handleFooterClick(buttonLabel) {
  const label = String(buttonLabel || '').toLowerCase();
  if (label === 'add') {
    addToProfiling();
  }
}

async function addToProfiling() {
  if (!selectedProfileId.value) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'Please select a profiling session',
      type: 'warning',
      position: 'top',
    });
    return;
  }

  if (selectedSessions.value.length === 0) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: 'No validation sessions selected',
      type: 'warning',
      position: 'top',
    });
    return;
  }

  let successCount = 0;
  let failureCount = 0;

  for (const session of selectedSessions.value) {
    const sessionId = getSessionId(session);
    if (!sessionId) {
      failureCount += 1;
      continue;
    }

    try {
      let response = await dataConnect.api('profile.add.val', {
        profile_id: Number(selectedProfileId.value),
        validate_session_id: sessionId,
      });

      if (response?.error) {
        response = await dataConnect.api('profile.add.val', {
          profile_id: Number(selectedProfileId.value),
          val_session_id: sessionId,
        });
      }

      if (response?.error) {
        throw new Error(response.error?.message || 'Failed to add validation session to profile');
      }

      successCount += 1;
    } catch (error) {
      failureCount += 1;
    }
  }

  if (successCount > 0) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: `Added ${successCount} validation session(s) to profiling`,
      type: 'success',
      position: 'top',
    });
  }

  if (failureCount > 0) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: `Failed to add ${failureCount} validation session(s)`,
      type: 'warning',
      position: 'top',
    });
  }

  if (successCount > 0) {
    emit('added', {
      profileId: Number(selectedProfileId.value),
      added: successCount,
      failed: failureCount,
    });
    closeModal();
  }
}

defineExpose({
  showModal,
  closeModal,
});
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
}

.modal-dialog {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.val-list-parent-wrap {
  height: 200px;
  min-height: 0;
}

.required::after {
  content: ' *';
  color: #cc0000;
}

</style>
